import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    alpha,
    useTheme,
    Tabs,
    Tab,
    IconButton,
    Chip,
    Avatar,
    Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    Undo as ReturnsIcon,
    AssignmentReturn as RMAIcon,
    Visibility as BlindIcon,
    Timeline as ForecastIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    MoreVert as MoreIcon,
    CheckCircle as ProcessedIcon,
    Schedule as PendingIcon,
    Error as AlertIcon,
    TrendingUp as TrendUpIcon,
    TrendingDown as TrendDownIcon,
    AutoGraph as AIProcessingIcon,
    LocalShipping as ShippingIcon,
    PhotoCamera as CameraIcon,
    ViewKanban as KanbanIcon,
    TableChart as TableIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../../components/common/PageWrapper';
import StatsCard, { StatsCardGrid } from '../../components/common/StatsCard';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import GlassPanel from '../../components/common/GlassPanel';
import KanbanView, { RETURN_KANBAN_COLUMNS } from '../../components/ui/KanbanView';
import {
    useReturns,
    useRMARequests,
    useBlindReturns,
    useReturnsMetrics,
    useCreateReturn,
    useProcessReturn,
    useReturnsForecast
} from '../../hooks/useReturns';

/**
 * ReturnsCockpitPage - High-fidelity Reverse Logistics Orchestration Center
 * Aurora 2026 Design Standard
 */
const ReturnsCockpitPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [viewMode, setViewMode] = useState('table');

    // Data Fetching
    const { data: returnsData, isLoading: returnsLoading, refetch: refetchReturns } = useReturns();
    const { data: rmaData, isLoading: rmaLoading, refetch: refetchRMA } = useRMARequests();
    const { data: blindData, isLoading: blindLoading, refetch: refetchBlind } = useBlindReturns();
    const { data: metrics, isLoading: metricsLoading } = useReturnsMetrics();

    const createReturnMutation = useCreateReturn();
    const processReturnMutation = useProcessReturn();
    
    const [createOpen, setCreateOpen] = useState(false);
    const [returnForm, setReturnForm] = useState({
        orderId: '',
        reason: 'damage',
        customer: '',
        notes: ''
    });

    const handleCreateSubmit = async () => {
        if (!returnForm.orderId) return;
        try {
            await createReturnMutation.mutateAsync({
                order_id: returnForm.orderId,
                reason: returnForm.reason,
                customer_id: returnForm.customer, // simplified
                notes: returnForm.notes,
                items: [] // should be selected from order
            });
            setCreateOpen(false);
            setReturnForm({ orderId: '', reason: 'damage', customer: '', notes: '' });
            refetchReturns();
        } catch (err) {
            console.error("Failed to create return", err);
        }
    };

    const handleProcessReturn = async (status) => {
        if (!selectedItem) return;
        try {
            await processReturnMutation.mutateAsync({
                returnId: selectedItem.id,
                status: status,
                notes: `Manual update to ${status}`
            });
            setIsSidebarOpen(false);
            refetchReturns();
        } catch (err) {
            console.error("Failed to update return", err);
        }
    };


    const isLoading = returnsLoading || rmaLoading || blindLoading || metricsLoading;

    // Memoized Data
    const returns = useMemo(() => Array.isArray(returnsData) ? returnsData : (returnsData?.returns || []), [returnsData]);
    const rmas = useMemo(() => Array.isArray(rmaData) ? rmaData : (rmaData?.rmas || []), [rmaData]);
    const blindReturns = useMemo(() => Array.isArray(blindData) ? blindData : (blindData?.blind_returns || []), [blindData]);

    // Transform returns for KanbanView
    const kanbanReturns = useMemo(() => returns.map(ret => ({
        ...ret,
        title: ret.order_number || `Return #${ret.id}`,
        description: `${ret.customer || 'Unknown'} - ${ret.reason || 'No reason'}`,
        tags: ret.reason ? [ret.reason] : []
    })), [returns]);

    // Handle view mode change
    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    }, []);

    // Handle Kanban status change (drag-and-drop)
    const handleKanbanStatusChange = useCallback(async (returnItem, newStatus) => {
        try {
            await processReturnMutation.mutateAsync({
                returnId: returnItem.id,
                status: newStatus,
                notes: `Status changed to ${newStatus} via Kanban`
            });
            refetchReturns();
        } catch (err) {
            console.error("Failed to update return status", err);
        }
    }, [processReturnMutation, refetchReturns]);

    // Tab Handlers
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSelectedItem(null);
        setIsSidebarOpen(false);
    };

    // Grid Columns Configuration
    const returnColumns = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'order_number', headerName: t('returns.orderNumber', 'Order #'), width: 130 },
        {
            field: 'customer',
            headerName: t('common.customer', 'Customer'),
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {params.value?.[0]}
                    </Avatar>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: 'status',
            headerName: t('common.status', 'Status'),
            width: 120,
            renderCell: (params) => {
                                const statusMap = {
                                    pending: { label: t('common.pending', 'Pending'), color: 'warning' },
                                    received: { label: t('common.received', 'Received'), color: 'info' },
                                    processing: { label: t('common.processing', 'Processing'), color: 'primary' },
                                    completed: { label: t('common.completed', 'Completed'), color: 'success' },
                                    rejected: { label: t('common.rejected', 'Rejected'), color: 'error' }
                                };
                const config = statusMap[params.value] || { label: params.value, color: 'default' };
                return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
            }
        },
        { field: 'reason', headerName: t('returns.reason', 'Reason'), width: 180 },
        { field: 'created_at', headerName: t('common.date', 'Date'), width: 150 }
    ];

        const rmaColumns = [
            { field: 'rma_number', headerName: t('returns.rmaNumber', 'RMA #'), width: 130 },
            { field: 'order_id', headerName: t('common.orderId', 'Order ID'), width: 110 },
            { field: 'type', headerName: t('common.type', 'Type'), width: 120 },
            {
                field: 'priority',
                headerName: t('common.priority', 'Priority'),
                width: 100,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        color={params.value === 'high' ? 'error' : 'default'}
                    />
                )
            },
            { field: 'status', headerName: t('common.status', 'Status'), width: 120 },
            { field: 'requested_at', headerName: t('common.requestedAt', 'Requested At'), width: 160 }
        ];

        const blindColumns = [
            { field: 'id', headerName: 'ID', width: 80 },
            { field: 'tracking_number', headerName: t('common.trackingNumber', 'Tracking #'), width: 180 },
            { field: 'carrier', headerName: t('common.carrier', 'Carrier'), width: 120 },
            {
                field: 'ai_confidence',
                headerName: t('ai.confidence', 'AI Confidence'),
                width: 130,
                renderCell: (params) => (
                    <Box sx={{ width: '100%' }}>
                        <Typography variant="caption">{Math.round(params.value * 100)}%</Typography>
                        <Box sx={{ height: 4, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, mt: 0.5 }}>
                            <Box sx={{ width: `${params.value * 100}%`, height: '100%', bgcolor: theme.palette.primary.main, borderRadius: 2 }} />
                        </Box>
                    </Box>
                )
            },
            { field: 'status', headerName: t('common.status', 'Status'), width: 130 },
            { field: 'received_at', headerName: t('common.receivedAt', 'Received At'), width: 160 }
        ];

    return (
        <PageWrapper
            title={t('returns.cockpit.title', 'Returns Cockpit')}
            subtitle={t('returns.cockpit.subtitle', 'Unified reverse logistics and RMA orchestration')}
            breadcrumbs={[
                { label: t('nav.admin', 'Admin'), path: '/admin' },
                { label: t('returns.cockpit.title', 'Returns Cockpit'), icon: ReturnsIcon }
            ]}
            actions={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                            refetchReturns();
                            refetchRMA();
                            refetchBlind();
                        }}
                    >
                        {t('common.refresh', 'Refresh')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                    >
                        {t('returns.newReturn', 'New Return')}
                    </Button>
                </Box>
            }
        >
            {/* KPI Cards Section */}
            <Box sx={{ mb: 4 }}>
                <StatsCardGrid columns={4}>
                    <StatsCard
                        icon={PendingIcon}
                        title={t('returns.pendingRMAs', 'Pending RMAs')}
                        value={isLoading ? <Skeleton width={40} /> : metrics?.pending_rmas || rmas.filter(r => r.status === 'pending').length}
                        trend="+5%"
                        trendColor="warning"
                        color={theme.palette.warning.main}
                    />
                    <StatsCard
                        icon={AIProcessingIcon}
                        title={t('returns.blindProcessing', 'Blind Backlog')}
                        value={isLoading ? <Skeleton width={40} /> : metrics?.blind_backlog || blindReturns.length}
                        subtitle={t('returns.processingViaAI', 'Processing via AI Vision')}
                        color={theme.palette.primary.main}
                    />
                    <StatsCard
                        icon={TrendUpIcon}
                        title={t('returns.reverseVelocity', 'Reverse Velocity')}
                        value={isLoading ? <Skeleton width={40} /> : `${metrics?.velocity || 24} units/hr`}
                        color={theme.palette.success.main}
                    />
                    <StatsCard
                        icon={ForecastIcon}
                        title={t('returns.forecastedInbound', 'Forecasted')}
                        value={isLoading ? <Skeleton width={40} /> : metrics?.forecast_inbound || 0}
                        subtitle={t('returns.next24hEstimation', 'Next 24h estimation')}
                        color={theme.palette.info.main}
                    />
                </StatsCardGrid>
            </Box>

            {/* Main Cockpit Interface */}
            <GlassPanel sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 400px)', minHeight: 600 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 64 }}>
                        <Tab
                            icon={<ReturnsIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('returns.tabs.active', 'Active Returns')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<RMAIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('returns.tabs.rma', 'RMA Requests')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<BlindIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={`${t('returns.tabs.blind', 'Blind Identification')} (${blindReturns.length})`}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<ForecastIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('returns.tabs.forecast', 'Forecasting')}
                            sx={{ minHeight: 64 }}
                        />
                    </Tabs>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {activeTab === 0 && (
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeChange}
                                size="small"
                            >
                                <ToggleButton value="table" aria-label="table view">
                                    <Tooltip title={t('common.tableView', 'Table View')}>
                                        <TableIcon fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="kanban" aria-label="kanban view">
                                    <Tooltip title={t('common.kanbanView', 'Kanban View')}>
                                        <KanbanIcon fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        <Tooltip title={t('common.filterView', 'Filter View')}>
                            <IconButton size="small"><FilterIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.moreActions', 'More Actions')}>
                            <IconButton size="small"><MoreIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        {/* Grid Area */}
                        <Box sx={{
                            flexGrow: 1,
                            height: '100%',
                            width: isSidebarOpen ? 'calc(100% - 400px)' : '100%',
                            transition: 'width 0.3s ease'
                        }}>
                            {activeTab === 0 && viewMode === 'table' && (
                                <SmartDataGrid
                                    rows={returns}
                                    columns={returnColumns}
                                    loading={returnsLoading}
                                    onRowClick={(params) => {
                                        setSelectedItem(params.row);
                                        setIsSidebarOpen(true);
                                    }}
                                />
                            )}
                            {activeTab === 0 && viewMode === 'kanban' && (
                                <Box sx={{ p: 2, overflowX: 'auto', height: '100%' }}>
                                    <KanbanView
                                        columns={RETURN_KANBAN_COLUMNS}
                                        items={kanbanReturns}
                                        groupBy="status"
                                        onStatusChange={handleKanbanStatusChange}
                                        onView={(item) => {
                                            setSelectedItem(item);
                                            setIsSidebarOpen(true);
                                        }}
                                        isLoading={returnsLoading}
                                    />
                                </Box>
                            )}
                            {activeTab === 1 && (
                                <SmartDataGrid
                                    rows={rmas}
                                    columns={rmaColumns}
                                    loading={rmaLoading}
                                    onRowClick={(params) => {
                                        setSelectedItem(params.row);
                                        setIsSidebarOpen(true);
                                    }}
                                />
                            )}
                            {activeTab === 2 && (
                                <SmartDataGrid
                                    rows={blindReturns}
                                    columns={blindColumns}
                                    loading={blindLoading}
                                    onRowClick={(params) => {
                                        setSelectedItem(params.row);
                                        setIsSidebarOpen(true);
                                    }}
                                />
                            )}
                            {activeTab === 3 && (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="h6" color="text.secondary">{t('common.returns.forecastingView', 'Reverse Logistics Forecasting View')}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {t('common.returns.forecastingDescription', 'Predictive analysis of incoming returns based on historical trends and shipment data.')}
                                    </Typography>
                                    {/* Forecast Chart Placeholder */}
                                    <Box sx={{ mt: 4, height: 300, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ForecastIcon sx={{ fontSize: 64, color: 'divider' }} />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Glass Sidebar */}
                        {isSidebarOpen && (
                            <Box sx={{
                                width: 400,
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(20px)',
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                animation: 'slideIn 0.3s ease-out'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6">
                                        {activeTab === 2 ? t('common.returns.blindIdentification', 'Blind Identification') : t('common.returns.returnDetail', 'Return Detail')}
                                    </Typography>
                                    <IconButton onClick={() => setIsSidebarOpen(false)} size="small">
                                        <MoreIcon sx={{ transform: 'rotate(90deg)' }} />
                                    </IconButton>
                                </Box>

                                {selectedItem && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">{t('common.returns.targetEntity', 'TARGET ENTITY')}</Typography>
                                            <Typography variant="h5" sx={{ mt: 0.5 }}>
                                                {selectedItem.rma_number || selectedItem.order_number || `#${selectedItem.id}`}
                                            </Typography>
                                            <Chip
                                                label={selectedItem.status}
                                                size="small"
                                                color="primary"
                                                sx={{ mt: 1 }}
                                            />
                                        </Box>

                                        {activeTab === 2 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Typography variant="subtitle2">{t('common.returns.aiSuggestions', 'AI Identification Suggestions')}</Typography>
                                                <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1), border: '1px solid', borderColor: theme.palette.success.main }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" fontWeight="bold">Order #8829</Typography>
                                                        <Typography variant="caption" color="success.main">94% Confidence</Typography>
                                                    </Box>
                                                    <Typography variant="caption">{t('common.returns.matchesDimensions', 'Matches: Dimensions, Tracking Origin')}</Typography>
                                                    <Button fullWidth size="small" variant="contained" sx={{ mt: 2 }} color="success">
                                                        {t('common.returns.linkToOrder', 'Link to Order')}
                                                    </Button>
                                                </Box>

                                                <Typography variant="subtitle2">{t('common.returns.physicalAttributes', 'Physical Attributes')}</Typography>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                                    <Box>
                                                        <Typography variant="caption">{t('common.carrier', 'Carrier')}</Typography>
                                                        <Typography variant="body2">{selectedItem.carrier}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption">{t('common.weight', 'Weight')}</Typography>
                                                        <Typography variant="body2">2.4 kg</Typography>
                                                    </Box>
                                                </Box>

                                                <Button startIcon={<CameraIcon />} variant="outlined" fullWidth>
                                                    {t('common.returns.rescanPackage', 'Rescan Package')}
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Typography variant="subtitle2">{t('common.returns.returnItems', 'Return Items')}</Typography>
                                                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                        <Skeleton variant="rectangular" width={40} height={40} />
                                                        <Box>
                                                            <Typography variant="body2">Industrial Sensor XL</Typography>
                                                            <Typography variant="caption">SKU: IND-399</Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                <Typography variant="subtitle2">{t('common.actions', 'Actions')}</Typography>
                                                <Button variant="contained" fullWidth startIcon={<ProcessedIcon />} onClick={() => handleProcessReturn('approved')}>
                                                    {t('common.returns.approveReturn', 'Approve Return')}
                                                </Button>
                                                <Button variant="outlined" fullWidth color="error" onClick={() => handleProcessReturn('rejected')}>
                                                    {t('common.returns.rejectWithReason', 'Reject with Reason')}
                                                </Button>
                                            </Box>
                                        )}

                                        <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                                            <Button fullWidth variant="text" onClick={() => navigate(`/admin/returns/${selectedItem.id}`)}>
                                                {t('common.returns.fullAuditTrail', 'Full Audit Trail')}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </GlassPanel>

            <style>
                {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
            </style>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{t('common.returns.createNewReturn', 'Create New Return')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                        <TextField
                            label={t('common.returns.orderIdNumber', 'Order ID / Number')}
                            fullWidth
                            value={returnForm.orderId}
                            onChange={(e) => setReturnForm({ ...returnForm, orderId: e.target.value })}
                        />
                         <FormControl fullWidth>
                            <InputLabel>{t('common.reason', 'Reason')}</InputLabel>
                            <Select
                                value={returnForm.reason}
                                label={t('common.reason', 'Reason')}
                                onChange={(e) => setReturnForm({ ...returnForm, reason: e.target.value })}
                            >
                                <MenuItem value="damage">{t('common.returns.reasons.damagedInTransit', 'Damaged in Transit')}</MenuItem>
                                <MenuItem value="defective">{t('common.returns.reasons.defective', 'Defective / Malfunction')}</MenuItem>
                                <MenuItem value="wrong_item">{t('common.returns.reasons.wrongItem', 'Wrong Item Sent')}</MenuItem>
                                <MenuItem value="changed_mind">{t('common.returns.reasons.changedMind', 'Customer Changed Mind')}</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label={t('common.returns.customerIdOptional', 'Customer ID (Optional)')}
                            fullWidth
                            value={returnForm.customer}
                            onChange={(e) => setReturnForm({ ...returnForm, customer: e.target.value })}
                        />
                        <TextField
                            label={t('common.returns.internalNotes', 'Internal Notes')}
                            fullWidth
                            multiline
                            rows={3}
                            value={returnForm.notes}
                            onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setCreateOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateSubmit}
                        disabled={!returnForm.orderId || createReturnMutation.isPending}
                    >
                        {createReturnMutation.isPending ? t('common.creating', 'Creating...') : t('common.returns.createReturnRequest', 'Create Return Request')}
                    </Button>
                </DialogActions>
            </Dialog>

        </PageWrapper>
    );
};

export default React.memo(ReturnsCockpitPage);